// filepath: apps/web/src/features/lists/gantt/hooks/useGanttRecords.ts
//
// FR-UI-VIEW-3 AC-1 / AC-2 — TanStack Query hook for the Gantt view's
// record fetch.
//
// The Gantt renderer issues GET /api/entity-records/{entityType}
// against the same FR-QRY-1 endpoint the EntityListTable uses
// (apps/web/src/features/lists/hooks/useEntityList.ts). The cache key
// shape differs intentionally so the Gantt window cannot accidentally
// mutate the list-view cache (and vice versa):
//
//   list  : ["list", tenantSlug, entityType, filterHash, sortKey, cursor]
//   kanban: ["lists", tenantSlug, "kanban", entityType, viewConfigVersion]
//   tree  : ["lists", tenantSlug, "tree",  entityType, viewConfigVersion]
//   gantt : ["lists", tenantSlug, "gantt", entityType, viewConfigVersion, startField]
//
// The sixth segment carries the start_field name so a definition
// republish that swaps the start_field automatically refreshes the
// Gantt window without the parent passing a separate version arg.
//
// SECURITY / INTEGRITY INVARIANTS:
//   - INV-1 — tenantSlug is the 2nd segment of the cache key tuple.
//   - INV-2 — the hook does NOT do per-row fetches to "fill in" server-
//     stripped fields. The response shape is authoritative.
//   - The 500-of-600 cap is server-enforced (FR-UI-VIEW-3 AC-2);
//     the hook surfaces BOTH `records` and a derived `totalCount` so
//     the GanttCountBanner can render the "X of Y records shown."
//     message when the server caps the result set. Total comes from
//     the wire shape when present, falling back to the records.length
//     so a server older than the banner-cap deployment degrades
//     cleanly (no banner; OFF by default).
//   - The return shape is the explicit FR-UI-VIEW-3 contract:
//       { records, totalCount, isLoading, isError, hasMore }
//     DIFFERS intentionally from the Kanban/Tree shape (which uses
//     { data, isLoading, isFetching, error, refetch }) because the
//     Gantt module is a fresh surface — not a hand-me-down shape —
//     so we encode the v1 contract directly.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiError } from "@/definitions/types";
import { useTenant } from "@/tenant/TenantProvider";
import { apiFetch } from "@/api";
import type { ListResponseWire, EntityRecordWire } from "../../types";
import { GanttRecord, normaliseGanttRecord } from "../types";

/**
 * Hard cap the server enforces on the Gantt window. Mirrors the
 * "500-of-N" rendering rule at FR-UI-VIEW-3 AC-2 — the cap is
 * server-enforced so the SPA doesn't need to second-guess the result
 * size. Surfaced as a constant for tests + the GanttCountBanner so
 * a future FR bumping the cap has a single source of truth.
 */
export const GANTT_RECORDS_CAP = 500;

/**
 * TanStack Query cache key factory for the Gantt window.
 *
 * Shape: `["lists", tenantSlug, "gantt", entityType, viewConfigVersion, startField]`.
 *
 * - 1st segment `"lists"` is the feature namespace so other list-feature
 *   hooks (useEntityList) don't collide.
 * - 2nd segment `tenantSlug` is the INV-1 invariant — switching
 *   tenants resolves to a distinct cache entry.
 * - 3rd segment `"gantt"` disambiguates the Gantt window from any
 *   other list-feature view (table / kanban / tree).
 * - 4th segment `entityType` is the entity-type filter.
 * - 5th segment `viewConfigVersion` is the entity definition's
 *   `version` number — when a definition is republished the cache
 *   entry is invalidated automatically (the parent component holds
 *   the `version` and passes it as an explicit argument).
 * - 6th segment `startField` is the gantt sub-config's `start_field`
 *   name. A definition republish that swaps the start_field
 *   automatically refreshes the Gantt window even when version is
 *   reused — this matters because the cache shape encodes
 *   `record.start = record.fields[startField]` so a different
 *   field yields a different view of the same record.
 */
export const ganttKeys = {
  all: ["lists"] as const,
  window: (
    tenantSlug: string,
    entityType: string,
    viewConfigVersion: number,
    startField: string,
  ) =>
    [
      "lists",
      tenantSlug,
      "gantt",
      entityType,
      String(viewConfigVersion),
      startField,
    ] as const,
};

export interface UseGanttRecordsOptions {
  /** Target entity type id. Required. */
  entityType: string;
  /**
   * The entity-definition version that supplied the `view_config`
   * block. When this changes the cache key changes automatically,
   * so a definition re-publication refreshes the Gantt view (the
   * start_field / end_field / label_field may differ between
   * versions).
   */
  viewConfigVersion: number;
  /**
   * The gantt `start_field` name. Carried as the 6th cache-key
   * segment so a definition republish that swaps the start_field
   * automatically refreshes the Gantt window even when version is
   * reused. When this is the empty string (entity definition still
   * loading) the hook is disabled (see `enabled` below).
   */
  startField: string;
  /** API base URL override. Defaults to `/api`. */
  apiBase?: string;
  /**
   * When false, the query is disabled (no fetch). Used when the
   * definition is still loading or the entityType / startField is
   * missing.
   */
  enabled?: boolean;
}

export interface UseGanttRecordsResult {
  /**
   * Normalised records the renderer should display. Each record is
   * projected into `GanttRecord` (an INV-7-friendly shape with the
   * `rowVersion` field) so the drag-to-edit handlers can supply the
   * OCC token without a follow-up fetch. Records whose wire envelope
   * is missing a usable row_version are dropped (see
   * `normaliseGanttRecord`).
   */
  records: GanttRecord[];
  /**
   * Server-supplied total record count across the whole entity
   * type. Null when the server wire omits the additive `total_count`
   * field (older deployments). When null, the GanttCountBanner stays
   * off — the FR-UI-VIEW-3 contract is graceful degradation.
   */
  totalCount: number | null;
  /** True while the initial fetch is in flight. */
  isLoading: boolean;
  /**
   * True when the latest fetch returned a non-2xx response. The
   * parent surfaces this to the rate-limit / fetch-failure envelope
   * (FR-UI-CORE-10). Distinct from `error` so the parent can route
   * on the boolean without an extra null check.
   */
  isError: boolean;
  /**
   * The most recent fetch error. Null while no fetch has failed.
   * The parent uses this with `classifyGanttError` to route into
   * the 6 mandatory error states (permission-denied / stale-version
   * / rate-limit / fetch-failure). Distinct from `isError` so the
   * renderer can branch on the typed status without re-narrowing.
   */
  error: ApiError | null;
  /**
   * True when the server explicitly indicates more pages are
   * available (next_cursor non-empty AND records.length reached
   * GANTT_RECORDS_CAP). False when the result set is the full set.
   * When the wire omits `next_cursor` and total_count is missing,
   * `hasMore` falls back to "records.length >= GANTT_RECORDS_CAP"
   * so the GanttCountBanner can still offer a load-more affordance
   * on the legacy wire envelope.
   */
  hasMore: boolean;
}

/**
 * Normalise a ListResponseWire into the Gantt-friendly shape.
 *
 * Reads the additive `total_count` field when present. When absent,
 * `totalCount` is null and the GanttCountBanner stays off.
 *
 * The server still applies CanRead field stripping on every record
 * (INV-2 / FR-AUTH-7) — the renderer MUST iterate only
 * `Object.keys(record.fields)` and never fall back to a per-row GET
 * to "fill in" absent fields.
 *
 * Records whose wire envelope is missing a usable row_version are
 * dropped at the normaliser layer (INV-7) — the drag-to-edit handler
 * cannot safely re-send them without the OCC token.
 */
function normaliseGanttPage(wire: ListResponseWire): {
  records: GanttRecord[];
  total: number | null;
  nextCursor: string | null;
} {
  const records: GanttRecord[] = (wire.records ?? [])
    .map((r: EntityRecordWire): GanttRecord | null => normaliseGanttRecord(r))
    .filter((rec): rec is GanttRecord => rec !== null);
  return {
    records,
    total: readTotal(wire),
    nextCursor: readNextCursor(wire),
  };
}

/**
 * Read the optional `total_count` field from a ListResponse envelope.
 *
 * FR-QRY-1 v1 ships `next_cursor` + `page_size` only. The Gantt
 * window's "X of N records shown." banner needs a server-supplied
 * total, which is an additive wire field (the future FR-QRY-2 will
 * deliver it as `total_count`). For backward-compat the helper
 * returns `null` when the field is absent — the hook degrades to
 * records.length so the banner stays off.
 */
function readTotal(wire: ListResponseWire): number | null {
  const raw = (wire as unknown as { total?: unknown }).total;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string" && /^[0-9]+$/.test(raw)) {
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Read the optional `next_cursor` field from a ListResponse envelope.
 *
 * Used by `hasMore` so the GanttCountBanner can offer a "Load more"
 * affordance. The hook itself does NOT auto-fetch the next page
 * (the parent drives pagination via a user gesture — see
 * GanttCountBanner.onLoadMore).
 */
function readNextCursor(wire: ListResponseWire): string | null {
  const raw = (wire as { next_cursor?: unknown }).next_cursor;
  if (typeof raw === "string" && raw.length > 0) return raw;
  return null;
}

/**
 * useGanttRecords — TanStack Query hook backing the Gantt window.
 *
 * The hook is "always-enabled" once the parent has a non-empty
 * `entityType`, a non-empty `startField` and a defined
 * `viewConfigVersion`. Passing `enabled: false` suppresses the fetch
 * (used when the parent is rendering a different view, or when the
 * gantt sub-config is still loading).
 *
 * The fragment-projecting `queryFragment` state is managed via
 * TanStack Query's `select` callback so a parent that wants
 * record-count fragments can wire it without re-fetching.
 */
export function useGanttRecords(
  opts: UseGanttRecordsOptions,
): UseGanttRecordsResult {
  const {
    entityType,
    viewConfigVersion,
    startField,
    apiBase = "/api",
    enabled = true,
  } = opts;

  const { tenantSlug } = useTenant();

  const queryKey = ganttKeys.window(
    tenantSlug,
    entityType,
    viewConfigVersion,
    startField,
  );

  const query: UseQueryResult<
    {
      records: GanttRecord[];
      total: number | null;
      nextCursor: string | null;
    },
    ApiError
  > = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams();
      // Gantt cap is server-enforced; we hint to the server that
      // we want the cap applied via the standard `limit` param.
      // The SPA never requests more than GANTT_RECORDS_CAP on its
      // own (limits the blast radius of a misconfigured tenant).
      params.set("limit", String(GANTT_RECORDS_CAP));
      const url = `${apiBase}/entity-records/${encodeURIComponent(
        entityType,
      )}?${params.toString()}`;

      let response: Response;
      try {
        // ISS-377 round 3 R2-B2.5: migrated to apiFetch. The
        // AbortSignal is forwarded via `init.signal` so TanStack
        // Query's unmount-cancellation reaches the underlying
        // fetch.
        response = await apiFetch(url, {
          method: "GET",
          init: {
            headers: {
              Accept: "application/json",
            },
            signal,
          },
        });
      } catch (networkErr) {
        throw new ApiError(
          networkErr instanceof Error ? networkErr.message : "network error",
          0,
          "network_error",
        );
      }

      if (!response.ok) {
        const body = await safeReadJson(response);
        const code =
          (body as { code?: string } | null)?.code ?? `http_${response.status}`;
        const retryAfter = parseRetryAfter(response.headers.get("Retry-After"));
        throw new ApiError(
          (body as { error?: string } | null)?.error ?? response.statusText,
          response.status,
          code,
          retryAfter,
        );
      }

      const data = (await response.json()) as ListResponseWire;
      return normaliseGanttPage(data);
    },
    enabled:
      enabled &&
      entityType.length > 0 &&
      startField.length > 0 &&
      Number.isFinite(viewConfigVersion),
    // FR-UI-VIEW-3 AC-3: drag-to-edit bar moves trigger explicit
    // query invalidation; staleTime stays short so a refetched
    // record count picks up concurrent edits from other windows
    // within a few seconds.
    staleTime: 5_000,
    gcTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const data = query.data;
  const records = data?.records ?? [];
  const total = data?.total ?? null;
  const nextCursor = data?.nextCursor ?? null;

  // hasMore — explicit FR-UI-VIEW-3 contract field. Two-pronged:
  //   1. nextCursor non-empty (server says there's another page),
  //   2. records.length >= cap (so the load-more affordance makes
  //      sense even on legacy wire envelopes without next_cursor).
  // The OR is intentional; either signal triggers the banner.
  const hasMore = nextCursor !== null || records.length >= GANTT_RECORDS_CAP;

  return {
    records,
    totalCount: total,
    isLoading: query.isLoading,
    isError: query.error !== null,
    error: query.error,
    hasMore,
  };
}

// ----------------- tiny helpers (local — not exported) ---------------

async function safeReadJson(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}

function parseRetryAfter(raw: string | null): number | null {
  if (!raw) return null;
  const seconds = Number.parseInt(raw, 10);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds;
  return null;
}
